import {
  Box3,
  BufferGeometry,
  CatmullRomCurve3,
  Color,
  ExtrudeGeometry,
  Float32BufferAttribute,
  Group,
  LatheGeometry,
  Material,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  Shape,
  TubeGeometry,
  Vector2,
  Vector3,
} from "three";
import { mergeGeometries } from "three/addons/utils/BufferGeometryUtils.js";

export type SunpatchMaterialKey = keyof ReturnType<typeof createSunpatchMaterials>;

export function createSunpatchMaterials() {
  const material = (name: string, color: number, roughness: number): MeshStandardMaterial => {
    const result = new MeshStandardMaterial({ color, metalness: 0, roughness });
    result.name = `sunpatch-${name}-material`;
    return result;
  };

  return {
    ink: material("ink", 0x172a24, 0.82),
    cream: material("cream", 0xfff8e7, 0.58),
    creamShadow: material("cream-shadow", 0xe7d8b4, 0.72),
    grass: material("grass", 0x68aa59, 0.88),
    grassDark: material("grass-dark", 0x376e42, 0.9),
    grassDry: material("grass-dry", 0xa7ad63, 0.9),
    soil: material("soil", 0xb97947, 0.92),
    soilDark: material("soil-dark", 0x75462f, 0.94),
    path: material("path", 0xe7c680, 0.9),
    pathEdge: material("path-edge", 0xc49a59, 0.91),
    wood: material("wood", 0x8a5636, 0.78),
    woodLight: material("wood-light", 0xb9824f, 0.78),
    woodDark: material("wood-dark", 0x563a29, 0.82),
    barn: material("barn", 0xb9433f, 0.8),
    barnDark: material("barn-dark", 0x7f2f2d, 0.84),
    corn: material("corn", 0xf4c542, 0.74),
    cornShadow: material("corn-shadow", 0xd89a2e, 0.78),
    leaf: material("leaf", 0x588a38, 0.8),
    leafLight: material("leaf-light", 0x7eaa45, 0.8),
    focus: material("focus", 0x087a86, 0.68),
    cash: material("cash", 0x43bd72, 0.8),
    gold: material("gold", 0xd99c26, 0.63),
    stone: material("stone", 0x7c7b69, 0.95),
    stoneLight: material("stone-light", 0xa6a183, 0.94),
    whiteChicken: material("white-chicken", 0xf7edcf, 0.79),
    brownChicken: material("brown-chicken", 0xb86a35, 0.82),
    charcoalChicken: material("charcoal-chicken", 0x3f4540, 0.86),
    comb: material("comb", 0xc84336, 0.74),
    beak: material("beak", 0xe9a22f, 0.72),
    skin: material("skin", 0xd99758, 0.78),
    hair: material("hair", 0x493126, 0.88),
    tealCloth: material("teal-cloth", 0x236c66, 0.86),
    brownCloth: material("brown-cloth", 0x624936, 0.88),
    straw: material("straw", 0xd8a84e, 0.85),
    strawDark: material("straw-dark", 0x9b6c2c, 0.88),
  } as const;
}

export type SunpatchMaterials = ReturnType<typeof createSunpatchMaterials>;

function roundedRectangleShape(width: number, height: number, radius: number, clippedCorner = 0): Shape {
  const halfWidth = width / 2;
  const halfHeight = height / 2;
  const r = Math.min(radius, halfWidth * 0.45, halfHeight * 0.45);
  const clip = Math.min(clippedCorner, width * 0.24, height * 0.24);
  const shape = new Shape();
  shape.moveTo(-halfWidth + r, -halfHeight);
  shape.lineTo(halfWidth - clip, -halfHeight);
  if (clip > 0) shape.lineTo(halfWidth, -halfHeight + clip);
  shape.lineTo(halfWidth, halfHeight - r);
  shape.quadraticCurveTo(halfWidth, halfHeight, halfWidth - r, halfHeight);
  shape.lineTo(-halfWidth + r, halfHeight);
  shape.quadraticCurveTo(-halfWidth, halfHeight, -halfWidth, halfHeight - r);
  shape.lineTo(-halfWidth, -halfHeight + r);
  shape.quadraticCurveTo(-halfWidth, -halfHeight, -halfWidth + r, -halfHeight);
  return shape;
}

export function createRoundedBlockGeometry(
  width: number,
  height: number,
  depth: number,
  radius: number,
  bevelSize = Math.min(radius * 0.45, depth * 0.18),
): ExtrudeGeometry {
  const geometry = new ExtrudeGeometry(roundedRectangleShape(width, height, radius), {
    bevelEnabled: bevelSize > 0,
    bevelSegments: 1,
    bevelSize,
    bevelThickness: bevelSize,
    curveSegments: 2,
    depth: Math.max(0.001, depth - bevelSize * 2),
    steps: 1,
  });
  geometry.translate(0, 0, -depth / 2 + bevelSize);
  geometry.computeVertexNormals();
  return geometry;
}

export function createGroundPatchGeometry(
  width: number,
  depth: number,
  thickness: number,
  radius: number,
  clippedCorner = 0,
): ExtrudeGeometry {
  const geometry = new ExtrudeGeometry(roundedRectangleShape(width, depth, radius, clippedCorner), {
    bevelEnabled: true,
    bevelSegments: 3,
    bevelSize: Math.min(0.08, thickness * 0.28),
    bevelThickness: Math.min(0.06, thickness * 0.22),
    curveSegments: 4,
    depth: thickness,
    steps: 1,
  });
  geometry.rotateX(Math.PI / 2);
  geometry.translate(0, -thickness * 0.55, 0);
  geometry.computeVertexNormals();
  return geometry;
}

export function createRibbonGeometry(points: readonly Vector3[], radius: number, thickness: number): TubeGeometry {
  const curve = new CatmullRomCurve3(points.map((point) => point.clone()), false, "catmullrom", 0.52);
  const geometry = new TubeGeometry(curve, Math.max(16, points.length * 10), radius, 8, false);
  geometry.scale(1, thickness / radius, 1);
  geometry.computeVertexNormals();
  return geometry;
}

export function createLeafGeometry(width: number, height: number, depth = 0.025): ExtrudeGeometry {
  const shape = new Shape();
  shape.moveTo(0, 0);
  shape.bezierCurveTo(-width * 0.62, height * 0.2, -width * 0.48, height * 0.72, 0, height);
  shape.bezierCurveTo(width * 0.48, height * 0.72, width * 0.62, height * 0.2, 0, 0);
  const geometry = new ExtrudeGeometry(shape, {
    bevelEnabled: true,
    bevelSegments: 1,
    bevelSize: depth * 0.45,
    bevelThickness: depth * 0.4,
    curveSegments: 5,
    depth,
    steps: 1,
  });
  geometry.translate(0, 0, -depth / 2);
  geometry.computeVertexNormals();
  return geometry;
}

export function createWingGeometry(width: number, height: number, depth = 0.04): ExtrudeGeometry {
  const shape = new Shape();
  shape.moveTo(-width * 0.42, height * 0.35);
  shape.bezierCurveTo(-width * 0.56, -height * 0.08, -width * 0.24, -height * 0.46, width * 0.14, -height * 0.36);
  shape.lineTo(width * 0.4, -height * 0.08);
  shape.lineTo(width * 0.27, height * 0.03);
  shape.lineTo(width * 0.48, height * 0.16);
  shape.lineTo(width * 0.16, height * 0.24);
  shape.bezierCurveTo(width * 0.03, height * 0.44, -width * 0.2, height * 0.48, -width * 0.42, height * 0.35);
  const geometry = new ExtrudeGeometry(shape, {
    bevelEnabled: true,
    bevelSegments: 1,
    bevelSize: 0.012,
    bevelThickness: 0.012,
    curveSegments: 4,
    depth,
  });
  geometry.translate(0, 0, -depth / 2);
  geometry.computeVertexNormals();
  return geometry;
}

export function createPearGeometry(width: number, height: number, segments = 16): LatheGeometry {
  const points: Vector2[] = [];
  const profile = [
    [0, 0], [0.34, 0.015], [0.48, 0.16], [0.52, 0.42], [0.45, 0.7], [0.3, 0.94], [0.08, 1],
  ] as const;
  for (const [radius, y] of profile) points.push(new Vector2(radius * width, y * height));
  const geometry = new LatheGeometry(points, segments);
  geometry.computeVertexNormals();
  return geometry;
}

export function createWedgeGeometry(width: number, height: number, depth: number): BufferGeometry {
  const hw = width / 2;
  const hd = depth / 2;
  const vertices = new Float32Array([
    -hw, 0, -hd, hw, 0, -hd, hw, 0, hd, -hw, 0, hd,
    -hw * 0.75, height, -hd * 0.7, hw * 0.75, height, -hd * 0.7, hw * 0.42, height * 0.72, hd, -hw * 0.42, height * 0.72, hd,
  ]);
  const indices = [0, 2, 1, 0, 3, 2, 4, 5, 6, 4, 6, 7, 0, 1, 5, 0, 5, 4, 1, 2, 6, 1, 6, 5, 2, 3, 7, 2, 7, 6, 3, 0, 4, 3, 4, 7];
  const geometry = new BufferGeometry();
  geometry.setAttribute("position", new Float32BufferAttribute(vertices, 3));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

export function createMesh(
  name: string,
  geometry: BufferGeometry,
  material: Material,
  castShadow = true,
  receiveShadow = true,
): Mesh {
  const mesh = new Mesh(geometry, material);
  mesh.name = name;
  mesh.castShadow = castShadow;
  mesh.receiveShadow = receiveShadow;
  return mesh;
}

export function nameNode<T extends Object3D>(node: T, name: string): T {
  node.name = name;
  return node;
}

export function batchStaticMeshes(
  root: Group,
  batchName: string,
  excludedRoots: ReadonlySet<Object3D>,
  orphanedGeometries: Set<BufferGeometry>,
): void {
  root.updateMatrixWorld(true);
  const rootInverse = root.matrixWorld.clone().invert();
  const byMaterial = new Map<Material, BufferGeometry[]>();
  const sourceMeshes: Mesh[] = [];

  const visit = (node: Object3D, excluded: boolean): void => {
    const isExcluded = excluded || excludedRoots.has(node);
    if (isExcluded) return;
    if ((node as Mesh).isMesh === true) {
      const mesh = node as Mesh;
      if (Array.isArray(mesh.material)) return;
      const transformed = mesh.geometry.index === null ? mesh.geometry.clone() : mesh.geometry.toNonIndexed();
      transformed.applyMatrix4(rootInverse.clone().multiply(mesh.matrixWorld));
      const geometries = byMaterial.get(mesh.material) ?? [];
      geometries.push(transformed);
      byMaterial.set(mesh.material, geometries);
      sourceMeshes.push(mesh);
    }
    for (const child of node.children) visit(child, isExcluded);
  };
  for (const child of [...root.children]) visit(child, false);

  for (const mesh of sourceMeshes) {
    orphanedGeometries.add(mesh.geometry);
    mesh.removeFromParent();
  }
  let batchIndex = 0;
  for (const [material, geometries] of byMaterial) {
    const merged = mergeGeometries(geometries, false);
    for (const geometry of geometries) geometry.dispose();
    if (merged === null) throw new Error(`Unable to batch ${batchName}`);
    const mesh = createMesh(`${batchName}-${++batchIndex}-${material.name}`, merged, material);
    root.add(mesh);
  }
}

export function collectAndDisposeOwnedResources(root: Object3D, orphanedGeometries: ReadonlySet<BufferGeometry>): () => void {
  const geometries = new Set<BufferGeometry>(orphanedGeometries);
  const materials = new Set<Material>();
  const textures = new Set<{ dispose(): void }>();
  root.traverse((node) => {
    const mesh = node as Mesh;
    if (mesh.isMesh !== true) return;
    geometries.add(mesh.geometry);
    const meshMaterials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    for (const material of meshMaterials) materials.add(material);
  });
  for (const material of materials) {
    for (const value of Object.values(material)) {
      if (value !== null && typeof value === "object" && "isTexture" in value && value.isTexture === true) {
        textures.add(value as { dispose(): void });
      }
    }
  }
  let disposed = false;
  return () => {
    if (disposed) return;
    disposed = true;
    for (const geometry of geometries) geometry.dispose();
    for (const material of materials) {
      material.dispose();
    }
    for (const texture of textures) texture.dispose();
    root.clear();
  };
}

export function setAuthoredMetadata(node: Object3D, assetNodeId: string): void {
  node.userData.assetNodeId = assetNodeId;
  node.userData.authorshipAttestation = "project-original-no-reference-reconstruction-v1";
}

export function assertFiniteBounds(root: Object3D): Box3 {
  root.updateMatrixWorld(true);
  return new Box3().setFromObject(root);
}

export function varyColor(material: MeshStandardMaterial, multiplier: number): MeshStandardMaterial {
  const clone = material.clone();
  clone.name = `${material.name}-variant-${multiplier.toFixed(2)}`;
  clone.color = new Color(material.color).multiplyScalar(multiplier);
  return clone;
}
